import { parseAgentControllerRef } from "@wakeloop/agent-runtime";
import type { LocalDaemonAdapterContainer } from "@wakeloop/local-service";
import { applyAcceptedLeases } from "@wakeloop/local-service";
import type {
  GetAgentProfilePrimaryBindingResponse,
  LocalRuntimeExpectedRoute,
  LocalRuntimeExpectedRouteExclusion,
  ModelCatalog,
  RuntimeTransportRegisterSessionResponse,
} from "@wakeloop/schemas/types";
import { listWakeLoopProfiles } from "../../config/wakeloop-profiles-api";
import { syncDeviceRuntimeStateProjection } from "../../local-service/reporting/device-runtime-state-sync";
import { resolveProfileWorkspaceRuntime } from "../../profile-workspace/runtime-resolution";
import type { Presenter } from "../../runtime/presenter/types";
import {
  createExpectedRoutesReadSession,
  type ExpectedRoutesReadSession,
} from "../../system/expected-routes-read-session";
import {
  createModelCatalogReadSession,
  type ModelCatalogReadSession,
} from "../../system/model-catalog-read-session";
import {
  createPrimaryBindingReadSession,
  type PrimaryBindingReadSession,
} from "../../system/primary-binding-read-session";
import { syncProfileWorkspaceState } from "../../system/profile-workspace-state";
import { emitRunLine, toErrorMessage } from "../core/logger";
import type {
  DaemonProfileRouteConfig,
  DaemonProfileRouteResolution,
  DaemonProfileSkipReason,
  MutableRouteCatalogState,
  RouteCatalog,
  RouteCatalogDiff,
  RouteCatalogSyncResult,
  SkippedDaemonProfile,
} from "../core/types";
import { DaemonServiceRunError } from "../core/types";
import {
  readLocalEnabledControllers,
  resolveLocalDaemonRouteCandidate,
  resolveLocalRouteCandidateFromBoundTarget,
} from "./resolve-controller-support";

// Phase durations for one catalog load. Callers pass a mutable collector so
// the daemon can journal where startup time actually went instead of
// inferring it from event gaps.
export type RouteCatalogLoadPhaseTimings = {
  candidateResolveMs?: number;
  expectedRoutesReadMs?: number;
  profilesListMs?: number;
  projectionRepairMs?: number;
  workspaceRepairMs?: number;
};

type CatalogRouteResolutionEntry = {
  nextResolvedRoute: DaemonProfileRouteResolution;
  profileName: string | null;
};

export async function loadDaemonRouteCatalog(input: {
  allowEmpty?: boolean;
  bindingReads?: PrimaryBindingReadSession;
  expectedRoutesRead?: ExpectedRoutesReadSession;
  localAdapterContainer: LocalDaemonAdapterContainer;
  localReplyStateMode?: "check" | "repair";
  modelCatalogRead?: ModelCatalogReadSession;
  ownerUserId: string;
  phaseTimings?: RouteCatalogLoadPhaseTimings;
  presenter: Presenter;
  quiet?: boolean;
}): Promise<RouteCatalog> {
  // One Core read per operation: the expected-routes projection when the
  // server offers it, otherwise the shared per-profile binding session. The
  // repair-mode device projection consults the same sessions, so repair and
  // catalog resolution never fan out twice.
  const expectedRoutesRead =
    input.expectedRoutesRead ?? createExpectedRoutesReadSession();
  const bindingReads = input.bindingReads ?? createPrimaryBindingReadSession();
  const modelCatalogRead =
    input.modelCatalogRead ?? createModelCatalogReadSession();
  if (input.localReplyStateMode === "repair") {
    await repairLocalReplyStateOrThrow({
      bindingReads,
      expectedRoutesRead,
      phaseTimings: input.phaseTimings,
      presenter: input.presenter,
    });
  }
  // A 5xx/network failure here rejects the whole load on purpose: a failed
  // sync keeps the currently registered set instead of shrinking it.
  const expectedRoutesReadStartedAt = Date.now();
  const expectedRoutesResult = await expectedRoutesRead.read();
  if (input.phaseTimings) {
    input.phaseTimings.expectedRoutesReadMs =
      Date.now() - expectedRoutesReadStartedAt;
  }
  const modelCatalog = await modelCatalogRead.read();

  const candidateResolveStartedAt = Date.now();
  const resolutions =
    expectedRoutesResult.status === "resolved"
      ? await resolveExpectedRouteResolutions({
          excludedProfiles: expectedRoutesResult.response.excludedProfiles,
          expectedRoutes: expectedRoutesResult.response.expectedRoutes,
          localAdapterContainer: input.localAdapterContainer,
          modelCatalog,
        })
      : await resolvePerProfileBindingResolutions({
          bindingReads,
          localAdapterContainer: input.localAdapterContainer,
          modelCatalog,
          ownerUserId: input.ownerUserId,
          phaseTimings: input.phaseTimings,
        });
  if (input.phaseTimings) {
    input.phaseTimings.candidateResolveMs =
      Date.now() - candidateResolveStartedAt;
  }

  const routeByProfileId = new Map<string, DaemonProfileRouteConfig>();
  const skippedProfiles: SkippedDaemonProfile[] = [];
  for (const { nextResolvedRoute, profileName } of resolutions) {
    if (nextResolvedRoute.status === "skipped") {
      if (nextResolvedRoute.profileId && nextResolvedRoute.skipReason) {
        skippedProfiles.push({
          profileId: nextResolvedRoute.profileId,
          profileName,
          skipReason: nextResolvedRoute.skipReason,
        });
      }
      if (
        !(
          input.quiet ||
          !nextResolvedRoute.profileId ||
          !nextResolvedRoute.skipReason
        )
      ) {
        emitRunLine({
          presenter: input.presenter,
          code: "daemon.run.profile_skipped",
          text: `skip profile ${nextResolvedRoute.profileId}: ${formatDaemonProfileSkipReason(
            nextResolvedRoute.skipReason
          )}`,
        });
      }
      continue;
    }
    routeByProfileId.set(nextResolvedRoute.profileId, nextResolvedRoute.route);
  }

  const catalog: RouteCatalog = {
    profileIds: Array.from(routeByProfileId.keys()).sort((left, right) =>
      left.localeCompare(right)
    ),
    routeByProfileId,
    skippedProfiles: skippedProfiles.sort((left, right) =>
      left.profileId.localeCompare(right.profileId)
    ),
  };
  if (input.allowEmpty === false && catalog.profileIds.length === 0) {
    throw new DaemonServiceRunError({
      code: "daemon.run.no_eligible_profiles",
      message:
        "no eligible local agent profiles for daemon run; enable agent controllers and bind agent profiles first",
    });
  }
  return catalog;
}

// Fast path: the server already decided binding eligibility (enabled_primary,
// active agent profile, THIS runtime); only the local half remains. Server
// exclusions map onto existing skip reasons where they describe a local-agent
// condition; other-runtime bindings are simply not this device's routes.
async function resolveExpectedRouteResolutions(input: {
  excludedProfiles: LocalRuntimeExpectedRouteExclusion[];
  expectedRoutes: LocalRuntimeExpectedRoute[];
  localAdapterContainer: LocalDaemonAdapterContainer;
  modelCatalog: ModelCatalog;
}): Promise<CatalogRouteResolutionEntry[]> {
  const localControllers = await readLocalEnabledControllers();
  const resolved = await Promise.all(
    input.expectedRoutes.map(async (expectedRoute) => {
      const parsedRef = parseAgentControllerRef(
        expectedRoute.executionTarget.agentControllerRef
      );
      if (!parsedRef) {
        return {
          nextResolvedRoute: {
            profileId: expectedRoute.agentProfileId,
            skipReason: "controller.binding.incomplete",
            status: "skipped",
          } satisfies DaemonProfileRouteResolution,
          profileName: expectedRoute.profileName,
        };
      }
      const localResolution = resolveLocalRouteCandidateFromBoundTarget({
        boundTarget: {
          agentControllerConversationId:
            expectedRoute.executionTarget.agentControllerConversationId,
          agentControllerRef: parsedRef.ref,
          wakeLoopServiceRuntimeId:
            expectedRoute.executionTarget.wakeLoopServiceRuntimeId,
          bindingId: expectedRoute.bindingId,
          agentControllerPermissionMode:
            expectedRoute.agentControllerPermissionMode,
          ...(expectedRoute.modelSelection
            ? { modelSelection: expectedRoute.modelSelection }
            : {}),
          profileId: expectedRoute.agentProfileId,
        },
        localAdapterContainer: input.localAdapterContainer,
        localControllers,
        modelCatalog: input.modelCatalog,
      });
      const nextResolvedRoute =
        localResolution.status === "accepted"
          ? await resolveCatalogRouteWorkspaceState({
              route: localResolution.route,
              profileId: localResolution.profileId,
            })
          : localResolution;
      return {
        nextResolvedRoute,
        profileName: expectedRoute.profileName,
      };
    })
  );

  const exclusionEntries: CatalogRouteResolutionEntry[] = [];
  for (const excluded of input.excludedProfiles) {
    if (excluded.reasonCode === "binding.other_runtime") {
      continue;
    }
    exclusionEntries.push({
      nextResolvedRoute: {
        profileId: excluded.agentProfileId,
        skipReason:
          excluded.reasonCode === "profile.inactive"
            ? "profile.status.inactive"
            : "controller.binding.disabled",
        status: "skipped",
      },
      profileName: null,
    });
  }

  return [...resolved, ...exclusionEntries];
}

// Fallback for servers without the expected-routes projection: enumerate the
// owner's profiles and read each primary binding through the shared session.
async function resolvePerProfileBindingResolutions(input: {
  bindingReads: PrimaryBindingReadSession;
  localAdapterContainer: LocalDaemonAdapterContainer;
  modelCatalog: ModelCatalog;
  ownerUserId: string;
  phaseTimings?: RouteCatalogLoadPhaseTimings;
}): Promise<CatalogRouteResolutionEntry[]> {
  const profilesListStartedAt = Date.now();
  const localControllers = await readLocalEnabledControllers();
  const profiles = await listWakeLoopProfiles();
  if (input.phaseTimings) {
    input.phaseTimings.profilesListMs = Date.now() - profilesListStartedAt;
  }

  return await Promise.all(
    profiles.map(async (profile) => {
      const profileName = normalizeSkippedProfileName(profile.profileName);
      const primaryBindingResult = await readPrimaryBindingForRouteCatalog({
        bindingReads: input.bindingReads,
        profile,
      });
      if (primaryBindingResult.status === "skipped") {
        return {
          nextResolvedRoute: {
            profileId: profile.wakeLoopProfileId,
            skipReason: primaryBindingResult.skipReason,
            status: "skipped",
          } satisfies DaemonProfileRouteResolution,
          profileName,
        };
      }
      const resolvedRoute = resolveLocalDaemonRouteCandidate({
        localAdapterContainer: input.localAdapterContainer,
        localControllers,
        modelCatalog: input.modelCatalog,
        ownerUserId: input.ownerUserId,
        primaryBinding: primaryBindingResult.binding,
        profile,
      });
      const nextResolvedRoute =
        resolvedRoute.status === "accepted"
          ? await resolveCatalogRouteWorkspaceState({
              route: resolvedRoute.route,
              profileId: resolvedRoute.profileId,
            })
          : resolvedRoute;
      return {
        nextResolvedRoute,
        profileName,
      };
    })
  );
}

async function readPrimaryBindingForRouteCatalog(input: {
  bindingReads: PrimaryBindingReadSession;
  profile: Awaited<ReturnType<typeof listWakeLoopProfiles>>[number];
}): Promise<
  | {
      binding: GetAgentProfilePrimaryBindingResponse["binding"] | null;
      status: "ready";
    }
  | {
      skipReason: Extract<
        DaemonProfileSkipReason,
        "profile.binding.read_unavailable"
      >;
      status: "skipped";
    }
> {
  if (input.profile.profileKind !== "agent") {
    return {
      binding: null,
      status: "ready",
    };
  }
  try {
    const response = await input.bindingReads.readPrimaryBinding(input.profile);
    if (response.status === "unavailable") {
      throw new Error(
        `Core primary binding read is unavailable for profile ${
          input.profile.wakeLoopProfileId
        }: ${response.binding.reasonCodes.join(", ")}`
      );
    }
    return {
      binding: response.binding,
      status: "ready",
    };
  } catch {
    return {
      skipReason: "profile.binding.read_unavailable",
      status: "skipped",
    };
  }
}

async function repairLocalReplyStateOrThrow(input: {
  bindingReads: PrimaryBindingReadSession;
  expectedRoutesRead: ExpectedRoutesReadSession;
  phaseTimings?: RouteCatalogLoadPhaseTimings;
  presenter: Presenter;
}): Promise<void> {
  const projectionRepairStartedAt = Date.now();
  try {
    await syncDeviceRuntimeStateProjection({
      bindingReads: input.bindingReads,
      expectedRoutesRead: input.expectedRoutesRead,
      mode: "repair",
    });
  } catch (error) {
    const errorMessage = toErrorMessage(error);
    emitRunLine({
      presenter: input.presenter,
      code: "daemon.run.projection_sync_failed",
      text: `local projection repair failed before route registration: ${errorMessage}`,
    });
    throw new DaemonServiceRunError({
      code: "daemon.run.projection_sync_failed",
      message: `local projection repair failed before route registration: ${errorMessage}`,
    });
  } finally {
    if (input.phaseTimings) {
      input.phaseTimings.projectionRepairMs =
        Date.now() - projectionRepairStartedAt;
    }
  }

  const workspaceRepairStartedAt = Date.now();
  try {
    await syncProfileWorkspaceState({
      mode: "repair",
    });
  } catch (error) {
    const errorMessage = toErrorMessage(error);
    emitRunLine({
      presenter: input.presenter,
      code: "daemon.run.profile_workspace_sync_failed",
      text: `local profile workspace repair failed before route registration: ${errorMessage}`,
    });
    throw new DaemonServiceRunError({
      code: "daemon.run.profile_workspace_sync_failed",
      message: `local profile workspace repair failed before route registration: ${errorMessage}`,
    });
  } finally {
    if (input.phaseTimings) {
      input.phaseTimings.workspaceRepairMs =
        Date.now() - workspaceRepairStartedAt;
    }
  }
}

export function buildRouteCatalogDiff(input: {
  currentProfileIds: string[];
  nextProfileIds: string[];
}): RouteCatalogDiff {
  const currentProfileIdsSet = new Set(input.currentProfileIds);
  const nextProfileIdsSet = new Set(input.nextProfileIds);
  const addedProfileIds = [...nextProfileIdsSet]
    .filter((profileId) => !currentProfileIdsSet.has(profileId))
    .sort((left, right) => left.localeCompare(right));
  const removedProfileIds = [...currentProfileIdsSet]
    .filter((profileId) => !nextProfileIdsSet.has(profileId))
    .sort((left, right) => left.localeCompare(right));
  const keptProfileIds = [...nextProfileIdsSet]
    .filter((profileId) => currentProfileIdsSet.has(profileId))
    .sort((left, right) => left.localeCompare(right));
  return {
    addedProfileIds,
    keptProfileIds,
    removedProfileIds,
  };
}

export function replaceRouteCatalogMap(input: {
  nextRouteByProfileId: RouteCatalog["routeByProfileId"];
  routeByProfileId: RouteCatalog["routeByProfileId"];
}): void {
  input.routeByProfileId.clear();
  for (const [profileId, routeConfig] of input.nextRouteByProfileId) {
    input.routeByProfileId.set(profileId, routeConfig);
  }
}

export function createMutableRouteCatalogState(
  routeCatalog: RouteCatalog
): MutableRouteCatalogState {
  return {
    fingerprint: buildRouteCatalogFingerprint(routeCatalog),
    profileIds: [...routeCatalog.profileIds],
    routeByProfileId: new Map(routeCatalog.routeByProfileId),
    skippedProfiles: [...routeCatalog.skippedProfiles],
  };
}

export function createRouteCatalogFromMutableState(
  state: MutableRouteCatalogState
): RouteCatalog {
  return {
    profileIds: [...state.profileIds],
    routeByProfileId: new Map(state.routeByProfileId),
    skippedProfiles: [...state.skippedProfiles],
  };
}

export async function syncDaemonRouteCatalogState(input: {
  catalogState: MutableRouteCatalogState;
  forceRegister?: boolean;
  leasesByProfileId: Map<string, number>;
  loadCatalog: () => Promise<RouteCatalog>;
  registerCatalog: (
    routeCatalog: RouteCatalog
  ) => Promise<RuntimeTransportRegisterSessionResponse>;
}): Promise<RouteCatalogSyncResult> {
  let nextCatalog: RouteCatalog;
  try {
    nextCatalog = await input.loadCatalog();
  } catch (error) {
    return {
      status: "failed",
      reason: "load_failed",
      errorMessage: toErrorMessage(error),
    };
  }

  const nextFingerprint = buildRouteCatalogFingerprint(nextCatalog);
  if (nextFingerprint === input.catalogState.fingerprint) {
    const shouldRefreshLeases =
      input.forceRegister ||
      nextCatalog.profileIds.some(
        (profileId) => !input.leasesByProfileId.has(profileId)
      );
    if (shouldRefreshLeases) {
      let registerResponse: RuntimeTransportRegisterSessionResponse;
      try {
        registerResponse = await input.registerCatalog(nextCatalog);
      } catch (error) {
        return {
          status: "failed",
          reason: "register_failed",
          errorMessage: toErrorMessage(error),
        };
      }

      applyAcceptedLeases({
        acceptedLeases: registerResponse.acceptedLeases,
        leasesByProfileId: input.leasesByProfileId,
      });
      replaceRouteCatalogMap({
        routeByProfileId: input.catalogState.routeByProfileId,
        nextRouteByProfileId: nextCatalog.routeByProfileId,
      });
      input.catalogState.profileIds = [...nextCatalog.profileIds];
      input.catalogState.fingerprint = nextFingerprint;
      input.catalogState.skippedProfiles = [...nextCatalog.skippedProfiles];
      return {
        registerResponse,
        skippedProfiles: nextCatalog.skippedProfiles,
        status: "registered",
      };
    }
    input.catalogState.skippedProfiles = [...nextCatalog.skippedProfiles];
    return {
      skippedProfiles: nextCatalog.skippedProfiles,
      status: "unchanged",
    };
  }

  let registerResponse: RuntimeTransportRegisterSessionResponse;
  try {
    registerResponse = await input.registerCatalog(nextCatalog);
  } catch (error) {
    return {
      status: "failed",
      reason: "register_failed",
      errorMessage: toErrorMessage(error),
    };
  }

  const diff = buildRouteCatalogDiff({
    currentProfileIds: input.catalogState.profileIds,
    nextProfileIds: nextCatalog.profileIds,
  });

  applyAcceptedLeases({
    acceptedLeases: registerResponse.acceptedLeases,
    leasesByProfileId: input.leasesByProfileId,
  });
  replaceRouteCatalogMap({
    routeByProfileId: input.catalogState.routeByProfileId,
    nextRouteByProfileId: nextCatalog.routeByProfileId,
  });
  input.catalogState.profileIds = [...nextCatalog.profileIds];
  input.catalogState.fingerprint = nextFingerprint;
  input.catalogState.skippedProfiles = [...nextCatalog.skippedProfiles];
  return {
    status: "changed",
    diff,
    registerResponse,
    skippedProfiles: nextCatalog.skippedProfiles,
  };
}

export function summarizeSkippedDaemonProfiles(
  skippedProfiles: SkippedDaemonProfile[]
): string {
  if (skippedProfiles.length === 0) {
    return "none";
  }
  const preview = skippedProfiles
    .slice(0, 3)
    .map((profile) => {
      const displayName = profile.profileName ?? profile.profileId;
      return `${displayName} (${profile.profileId}): ${formatDaemonProfileSkipReason(
        profile.skipReason
      )}`;
    })
    .join("; ");
  const remainingCount = skippedProfiles.length - 3;
  if (remainingCount <= 0) {
    return preview;
  }
  return `${preview}; +${String(remainingCount)} more`;
}

async function resolveCatalogRouteWorkspaceState(input: {
  route: DaemonProfileRouteConfig;
  profileId: string;
}): Promise<
  Extract<DaemonProfileRouteResolution, { status: "accepted" | "skipped" }>
> {
  const workspace = await resolveProfileWorkspaceRuntime({
    wakeLoopProfileId: input.profileId,
    mode: "inspect",
  });
  if (workspace.status === "missing") {
    return {
      profileId: input.profileId,
      skipReason: "workspace.missing",
      status: "skipped",
    };
  }
  if (workspace.status === "invalid") {
    return {
      profileId: input.profileId,
      skipReason: "workspace.invalid",
      status: "skipped",
    };
  }

  return {
    profileId: input.profileId,
    route: input.route,
    status: "accepted",
  };
}

function formatDaemonProfileSkipReason(
  skipReason: DaemonProfileSkipReason
): string {
  switch (skipReason) {
    case "projection.missing":
      return "local device runtime projection is missing";
    case "controller.registry.disabled":
      return "controller is disabled in the local agents registry";
    case "controller.launch.needs_repair":
      return "controller launch needs repair on this device";
    case "runtime.adapter.unsupported":
      return "local runtime adapter support is missing on this device";
    case "controller.binding.disabled":
      return "controller binding is disabled";
    case "controller.binding.incomplete":
      return "controller binding metadata is incomplete";
    case "profile.status.inactive":
      return "profile is not active";
    case "profile.binding.read_unavailable":
      return "profile binding could not be read";
    case "profile.binding.unbound":
      return "profile binding is not bound";
    case "workspace.missing":
      return "local profile workspace is missing";
    case "workspace.invalid":
      return "local profile workspace is invalid";
    default:
      return skipReason;
  }
}

function normalizeSkippedProfileName(value: string): string | null {
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function buildRouteCatalogFingerprint(routeCatalog: RouteCatalog): string {
  const entries = routeCatalog.profileIds.map((profileId) => {
    const route = routeCatalog.routeByProfileId.get(profileId);
    return {
      profileId,
      bindingId: route?.bindingId ?? null,
      wakeLoopServiceRuntimeId: route?.wakeLoopServiceRuntimeId ?? null,
      agentControllerRef: route?.agentControllerRef ?? null,
      agentControllerConversationId:
        route?.agentControllerConversationId ?? null,
      agentControllerPermissionMode:
        route?.agentControllerPermissionMode ?? null,
      conversationPolicy: route?.conversationPolicy ?? null,
      streamingMode: route?.streamingMode ?? null,
      transportMode: route?.transportMode ?? null,
      contextIdMappingSpec: route?.contextIdMappingSpec
        ? {
            sourceField: route.contextIdMappingSpec.sourceField,
            mappingVersion: route.contextIdMappingSpec.mappingVersion,
          }
        : null,
    };
  });
  const skippedProfiles = routeCatalog.skippedProfiles.map((profile) => ({
    profileId: profile.profileId,
    profileName: profile.profileName,
    skipReason: profile.skipReason,
  }));
  return JSON.stringify({ entries, skippedProfiles });
}
